import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { getUtilityRates } from "@/lib/api";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl bg-white border border-[#E5E0D8] p-3 shadow-lg text-xs">
      <div className="font-serif-display text-sm text-[#0F382C]">{p.label}</div>
      <div className="font-mono-metric mt-1 text-[#D97706]">+${p.amount.toFixed(2)}/mo</div>
    </div>
  );
};

function UtilityChart({ utility, testId }) {
  const rows = utility.increases.flatMap((yr) =>
    yr.components.map((c) => ({
      year: yr.year,
      label: `${yr.year} · ${c.label}`,
      amount: c.amount,
    }))
  );
  return (
    <div className="card-warm p-6 md:p-8" data-testid={testId}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="eyebrow">Utility · {utility.example_kwh} kWh / mo example</div>
          <h3 className="mt-1 font-serif-display text-2xl text-[#0F382C]">{utility.utility}</h3>
        </div>
        <div className="flex gap-3">
          {utility.increases.map((yr) => (
            <div key={yr.year} className="rounded-xl bg-[#F7F4EB] px-4 py-3 border border-[#E5E0D8]">
              <div className="font-mono-metric text-[10px] uppercase tracking-widest text-slate-500">{yr.year}</div>
              <div className="font-serif-display text-lg text-[#0F382C]">+${yr.monthly_increase.toFixed(2)}/mo</div>
              <div className="font-mono-metric text-sm font-bold text-[#B45309]">${yr.annualized.toFixed(2)}/yr</div>
            </div>
          ))}
        </div>
      </div>

      <div className="h-72 w-full">
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#64748B" }}
              angle={-25}
              textAnchor="end"
              height={70}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#64748B", fontFamily: "JetBrains Mono" }}
              tickFormatter={(v) => `$${v}`}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(217,119,6,0.06)" }} />
            <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
              {rows.map((r, i) => (
                <Cell key={i} fill={r.year === 2025 ? "#D97706" : "#0F382C"} />
              ))}
              <LabelList
                dataKey="amount"
                position="top"
                formatter={(v) => `$${v.toFixed(2)}`}
                style={{ fontSize: 10, fill: "#475569", fontFamily: "JetBrains Mono" }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 text-xs text-slate-500">
        Source: {utility.increases[0].source} · Last updated Jan 15, 2026
      </div>
    </div>
  );
}

export default function UtilityRateCharts() {
  const [data, setData] = useState(null);
  useEffect(() => { getUtilityRates().then(setData).catch(() => {}); }, []);
  if (!data) return null;

  return (
    <section className="py-20 md:py-28 bg-[#FDFBF7]" data-testid="utility-rates-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="eyebrow">Utility Rate Pressure</div>
          <h2 className="mt-3 font-serif-display text-3xl md:text-5xl font-semibold text-[#0F382C]">
            The rate you pay today isn't the rate you'll pay tomorrow.
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Approved and filed increases for two of Virginia's largest utilities, based on a presentation example of 1,000 kWh per month.
          </p>
        </div>

        <div className="mt-12 grid gap-8">
          <UtilityChart utility={data.dominion} testId="chart-dominion" />
          <UtilityChart utility={data.rec} testId="chart-rec" />
        </div>

        <p className="mt-6 text-xs text-slate-500 max-w-3xl">{data.disclaimer}</p>
      </div>
    </section>
  );
}
